import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import CookieBanner from "@/components/CookieBanner";
import LegalConsentModal from "@/components/LegalConsentModal";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { lazy, Suspense, useEffect } from "react";
import Login from "@/pages/Login";
import Layout from "@/pages/Layout";
import AppLayout from "@/pages/app/AppLayout";
import { AppAuthProvider } from "@/context/AppAuthContext";
import AppErrorBoundary from "@/components/app/AppErrorBoundary";
import AppPermGuard from "@/components/app/AppPermGuard";

// 🚀 PERF 2026-10-14 — páginas carregadas sob demanda (code splitting): o navegador
// baixa só o código da tela aberta, em vez do sistema inteiro no primeiro acesso.
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const SuperAdmin = lazy(() => import("@/pages/SuperAdmin"));
const LoginsSuspeitos = lazy(() => import("@/pages/LoginsSuspeitos"));
const SegurancaSuperAdmin = lazy(() => import("@/pages/SegurancaSuperAdmin"));
const Empresa = lazy(() => import("@/pages/Empresa"));
const Departamentos = lazy(() => import("@/pages/Departamentos"));
const Funcionarios = lazy(() => import("@/pages/Funcionarios"));
const Equipamentos = lazy(() => import("@/pages/Equipamentos"));
const Feriados = lazy(() => import("@/pages/Feriados"));
const Justificativas = lazy(() => import("@/pages/Justificativas"));
const Horarios = lazy(() => import("@/pages/Horarios"));
const ConfigCalculo = lazy(() => import("@/pages/ConfigCalculo"));
const CartaoPonto = lazy(() => import("@/pages/CartaoPonto"));
const Monitoramento = lazy(() => import("@/pages/Monitoramento"));
const MonitoramentoGlobal = lazy(() => import("@/pages/MonitoramentoGlobal"));
const AtualizacaoColetores = lazy(() => import("@/pages/AtualizacaoColetores"));
const RecuperacaoGeral = lazy(() => import("@/pages/RecuperacaoGeral"));
const PainelComprovantes = lazy(() => import("@/pages/PainelComprovantes"));
const Relatorios = lazy(() => import("@/pages/Relatorios"));
const RelatorioAdicionalNoturno = lazy(() => import("@/pages/RelatorioAdicionalNoturno"));
const RelatoriosNovos = lazy(() => import("@/pages/RelatoriosNovos"));
const ImpressaoFolha = lazy(() => import("@/pages/ImpressaoFolha"));
const FilaSync = lazy(() => import("@/pages/FilaSync"));
const WsLive = lazy(() => import("@/pages/WsLive"));
const VpsHealth = lazy(() => import("@/pages/VpsHealth"));
const WsEvoDiagnostico = lazy(() => import("@/pages/WsEvoDiagnostico"));
const Fiscal = lazy(() => import("@/pages/Fiscal"));
const Termos = lazy(() => import("@/pages/Termos"));
const RepImport = lazy(() => import("@/pages/RepImport"));
const Filiais = lazy(() => import("@/pages/Filiais"));
const Mobile = lazy(() => import("@/pages/Mobile"));
const RevisaoFraude = lazy(() => import("@/pages/RevisaoFraude"));
const Diagnostico = lazy(() => import("@/pages/Diagnostico"));
const Funcoes = lazy(() => import("@/pages/Funcoes"));
const BancoHoras = lazy(() => import("@/pages/BancoHoras"));
const PainelBanco = lazy(() => import("@/pages/PainelBanco"));
const AppUsuarios = lazy(() => import("@/pages/AppUsuarios"));
const AppLogin = lazy(() => import("@/pages/app/AppLogin"));
const AppDashboard = lazy(() => import("@/pages/app/AppDashboard"));
const AppHistorico = lazy(() => import("@/pages/app/AppHistorico"));
const AppSettings = lazy(() => import("@/pages/app/AppSettings"));
const AppBaterPonto = lazy(() => import("@/pages/app/AppBaterPonto"));
const AppAtestado = lazy(() => import("@/pages/app/AppAtestado"));
const AppJustificativa = lazy(() => import("@/pages/app/AppJustificativa"));
const AppJustificativas = lazy(() => import("@/pages/app/AppJustificativas"));
const KioskFacial = lazy(() => import("@/pages/KioskFacial"));
const AppFacialColetivo = lazy(() => import("@/pages/AppFacialColetivo"));
const CoAdministradores = lazy(() => import("@/pages/CoAdministradores"));

const PageFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center text-slate-400 text-sm" data-testid="page-loading">Carregando…</div>
);

const qc = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } });

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// 🆕 Guard global: se o usuário instalou o PWA do Kiosk (`/kiosk/{token}`) mas o
// atalho ficou salvo só com o domínio raiz (`digiponto.net`), ao abrir o atalho
// cai aqui em `/` ANTES de qualquer autenticação. Esse guard detecta modo
// standalone + token salvo em localStorage e redireciona pro /kiosk/{token} correto.
// Importante: só redireciona em modo standalone — abrir `digiponto.net` no
// navegador normal continua levando ao login do admin.
function KioskStandaloneRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    try {
      if (window.location.pathname !== "/") return;
      const isStandalone =
        window.matchMedia?.("(display-mode: standalone)")?.matches ||
        window.matchMedia?.("(display-mode: fullscreen)")?.matches ||
        window.navigator.standalone === true;
      if (!isStandalone) return;
      const installed = localStorage.getItem("digiponto_installed_as_kiosk") === "1";
      const token = localStorage.getItem("digiponto_kiosk_token");
      if (installed && token) {
        navigate(`/kiosk/${token}`, { replace: true });
      }
    } catch {}
  }, [navigate]);
  return null;
}

function App() {
  // Remove o splash de boot (public/index.html) assim que o React mounta.
  // Fade 400ms + remove do DOM para liberar o z-index e permitir cliques.
  useEffect(() => {
    const splash = document.getElementById("digiponto-splash");
    if (splash) {
      splash.classList.add("hidden");
      setTimeout(() => splash.remove(), 450);
    }
  }, []);

  return (
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <BrowserRouter>
          <KioskStandaloneRedirect />
          <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/politica-privacidade" element={<PrivacyPolicy />} />
            <Route path="/termos-de-uso" element={<TermsOfService />} />
            <Route path="/mobile" element={<Mobile />} />
            {/* App Facial Coletivo (modo kiosk) — rota pública sem auth */}
            <Route path="/kiosk/:token" element={<KioskFacial />} />
            {/* PWA do funcionário (login por usuário/senha gerenciado pelo admin) */}
            <Route path="/app" element={<AppErrorBoundary><AppAuthProvider><AppLayout /></AppAuthProvider></AppErrorBoundary>}>
              <Route index element={<AppDashboard />} />
              <Route path="login" element={<AppLogin />} />
              <Route path="historico" element={<AppPermGuard perms={["perm_consultar"]}><AppHistorico /></AppPermGuard>} />
              <Route path="settings" element={<AppSettings />} />
              <Route path="bater" element={<AppBaterPonto />} />
              <Route path="bater-simples" element={<Navigate to="/app/bater" replace />} />
              <Route path="atestado" element={<AppPermGuard perms={["perm_atestados"]}><AppAtestado /></AppPermGuard>} />
              <Route path="justificativa" element={<AppPermGuard perms={["perm_justificativas"]}><AppJustificativa /></AppPermGuard>} />
              <Route path="justificativas" element={<AppPermGuard perms={["perm_atestados", "perm_justificativas"]}><AppJustificativas /></AppPermGuard>} />
            </Route>
            <Route path="/" element={<Protected><Layout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="superadmin" element={<SuperAdmin />} />
              <Route path="logins-suspeitos" element={<LoginsSuspeitos />} />
              <Route path="superadmin/seguranca" element={<SegurancaSuperAdmin />} />
              <Route path="empresa" element={<Empresa />} />
              <Route path="departamentos" element={<Departamentos />} />
              <Route path="funcoes" element={<Funcoes />} />
              <Route path="filiais" element={<Filiais />} />
              <Route path="co-administradores" element={<CoAdministradores />} />
              <Route path="funcionarios" element={<Funcionarios />} />
              <Route path="equipamentos" element={<Equipamentos />} />
              <Route path="diagnostico" element={<Diagnostico />} />
              <Route path="feriados" element={<Feriados />} />
              <Route path="justificativas" element={<Justificativas />} />
              <Route path="horarios" element={<Horarios />} />
              <Route path="config-calculo" element={<ConfigCalculo />} />
              <Route path="cartao-ponto" element={<CartaoPonto />} />
              <Route path="monitoramento" element={<Monitoramento />} />
              <Route path="revisao-fraude" element={<RevisaoFraude />} />
              <Route path="superadmin/monitoramento-global" element={<MonitoramentoGlobal />} />
              <Route path="superadmin/atualizacao-coletores" element={<AtualizacaoColetores />} />
              <Route path="superadmin/recuperacao-geral" element={<RecuperacaoGeral />} />
              <Route path="superadmin/comprovantes" element={<PainelComprovantes />} />
              <Route path="banco-horas" element={<BancoHoras />} />
              <Route path="painel-banco" element={<PainelBanco />} />
              <Route path="app-usuarios" element={<AppUsuarios />} />
              <Route path="relatorios" element={<Relatorios />} />
              <Route path="relatorios/adicional-noturno" element={<RelatorioAdicionalNoturno />} />
              <Route path="relatorios/faltas" element={<RelatoriosNovos tipo="faltas" />} />
              <Route path="relatorios/horas-extras" element={<RelatoriosNovos tipo="horas-extras" />} />
              <Route path="relatorios/atestados" element={<RelatoriosNovos tipo="atestados" />} />
              <Route path="impressao-folha" element={<ImpressaoFolha />} />
              <Route path="app-facial-coletivo" element={<AppFacialColetivo />} />
              <Route path="fiscal" element={<Fiscal />} />
              <Route path="rep-importar" element={<RepImport />} />
              <Route path="fila-sync" element={<FilaSync />} />
              <Route path="ws-live" element={<WsLive />} />
              <Route path="vps-health" element={<VpsHealth />} />
              <Route path="superadmin/ws-evo-diagnostico" element={<WsEvoDiagnostico />} />
              <Route path="termos" element={<Termos />} />
            </Route>
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
        <CookieBanner />
        <LegalConsentModal />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
